import Cookie from 'js-cookie';
import { AxiosResponse } from 'axios';
import qs from 'qs';
import { every, noop } from 'lodash-es';

import {
  serializableLocalStorage,
  serializableSessionStorage,
} from '../storage';
import { user } from '../user';
import { httpClient } from '../http';
import { IS_MQQ, IS_WEIXIN, IS_ZXG } from '../platform/environment';
import { sdk } from '../platform';

const URL = require('url');
const COOKIE_KEYS = (process.env.VUE_APP_AUTH_KEYS as string).split(/\s*,\s*/);

const RELOGIN_KEY = 'stock://relogin';
const MAX_RELOGIN_COUNT = 7;

class Auth {
  jumped = false;

  constructor(private readonly storage: Storage) {}

  /***
   * 获取cookie中的用户登录态信息
   * @returns {{}}
   */
  getLoginInfo() {
    const info: any = {};
    COOKIE_KEYS.forEach((key: string) => {
      info[key] = Cookie.get(key);
    });
    return info;
  }

  /***
   * 根据cookie内容判断用户是否已经登录
   * @returns {boolean}
   */
  isLogin() {
    const loginInfo = this.getLoginInfo();
    return every(COOKIE_KEYS, (key) => !!loginInfo[key]);
  }

  get returnUrl() {
    const retUrl = location.href.split(location.host)[1].replace(/^\//, '');
    const { pathname, query = '', hash = '' } = URL.parse(retUrl);
    const urlQuery = qs.parse(query);
    const extendedQuery = Object.assign(urlQuery, { _: Date.now() });
    return `${pathname}?${qs.stringify(extendedQuery)}${hash}`;
  }

  /***
   * 跳转登录
   * @returns {Promise.<void>}
   */
  async login() {
    if (IS_WEIXIN) {
      return this.loginForWeChat();
    } else if (IS_ZXG) {
      return this.loginForPortfolio();
    } else if (IS_MQQ) {
      return this.loginForQQ();
    }
  }

  /***
   * 跳转登录
   * @returns {Promise.<void>}
   */
  async loginForWeChat() {
    if (this.isLogin()) return;
    if (this.jumped) return new Promise(noop);
    this.jumped = true;
    await this.logout();
    const baseUrl = process.env.VUE_APP_WX_LOGIN_JUMP_URL;
    const queryParams = {
      returl: this.returnUrl,
    };

    location.href = [baseUrl, qs.stringify(queryParams)].join('?');
    return Promise.resolve();
  }

  /***
   * 跳转登录
   * @returns {Promise.<void>}
   */
  async loginForQQ() {
    if (this.isLogin()) return;
    if (this.jumped) return new Promise(noop);
    this.jumped = true;
    await this.logout();
    const baseUrl = process.env.VUE_APP_WX_LOGIN_JUMP_URL;
    const queryParams = {
      returl: this.returnUrl,
    };

    location.href = [baseUrl, qs.stringify(queryParams)].join('?');
    return new Promise(() => null);
  }

  /***
   * 刷新自选股app登录态，会打开登录弹窗
   */
  async refreshPortfolioAppLoginTicket() {
    return new Promise((resolve, reject) => {
      window.StockJSBridge.invoke('refreshLoginTicket', {}, async (data) => {
        if (data && data.err_msg === 'refreshLoginTicket:ok') {
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  async loginForPortfolio() {
    const baseUrl = process.env.VUE_APP_ZXG_LOGIN_JUMP_URL;
    return new Promise((resolve, reject) => {
      sdk.waitForJSBridge().then(() => {
        window.StockJSBridge.invoke('login', {}, async (data) => {
          switch (data.err_msg) {
            case 'login:ok': {
              const uniqueId = [data.type, data.openid || data.uin].join(':');
              const prevUniqueId = serializableLocalStorage.getItem(
                '__zxg_uid__',
              );
              // 用户已经登录
              if (this.isLogin() && uniqueId === prevUniqueId) {
                resolve();
                return;
              }
              await this.logout();
              serializableLocalStorage.setItem('__zxg_uid__', uniqueId);
              const loginParams: any = {
                app: 'zxg',
                // returl: this.returnUrl, // 这里不需要returl了，因为自选股app_login的跳转登录有问题
              };
              switch (data.type) {
                case 'wx':
                  Object.assign(loginParams, {
                    login_type: 3,
                    openid: data.openid,
                    access_token: data.token || data.accessToken,
                  });
                  break;
                case 'qq':
                  if (data.accessToken && data.appid) {
                    Object.assign(loginParams, {
                      login_type: 2,
                      openid: data.openid,
                      access_token: data.accessToken,
                    });
                  } else {
                    Object.assign(loginParams, {
                      login_type: 1,
                      luin: data.luin,
                      lskey: data.lskey,
                    });
                  }
                  break;
              }
              await httpClient
                .request({
                  url: baseUrl,
                  method: 'post',
                  data: loginParams,
                  appLogin: true,
                } as any)
                .then(resolve, () =>
                  this.refreshPortfolioAppLoginTicket().then(() =>
                    this.loginForPortfolio(),
                  ),
                );
              break;
            }
            case 'login:fail':
              await this.logout();
              reject();
              break;
          }
        });
      });
    });
  }

  /***
   * 获取登录态
   * @returns {Promise.<void>}
   */
  async requireLogin() {
    return await this.login();
  }

  /***
   * 退出登录（清空当前用户登录态）
   */
  async logout() {
    // 清除缓存的userInfo
    user.clean();
    // 清除登录重试次数
    this.storage.removeItem(RELOGIN_KEY);
    // 清除cookie
    COOKIE_KEYS.forEach((key: string) => {
      Cookie.remove(key);
      Cookie.remove(key, {
        path: '/',
        domain: process.env.VUE_APP_COOKIE_DOMAIN,
      });
    });
  }
}

export const auth = new Auth(serializableSessionStorage);

// 绑定http登录错误请求，尝试登录并重试请求
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data } = response;
    if (data && /^510010\d\d$/.test(data['retcode'])) {
      const reLoginCount = serializableSessionStorage.getItem(RELOGIN_KEY) || 0;
      // 重复登录次数超过限制
      if (reLoginCount >= MAX_RELOGIN_COUNT) {
        // alert(data.retmsg);
        return Promise.reject(response);
      }
      serializableSessionStorage.setItem(RELOGIN_KEY, reLoginCount + 1);
      // 尝试登录并重试请求
      auth.logout();
      auth.login();
      return new Promise(noop);
    } else {
      serializableSessionStorage.removeItem(RELOGIN_KEY);
    }
    return response;
  },
  (response: AxiosResponse) => {
    const { data, config } = response;
    // app_login的登录报错要忽略，避免死循环
    if (
      data &&
      /^510010\d\d$/.test(data['retcode']) &&
      !(config as any).appLogin
    ) {
      const reLoginCount = serializableSessionStorage.getItem(RELOGIN_KEY) || 0;
      // 重复登录次数超过限制
      if (reLoginCount >= MAX_RELOGIN_COUNT) {
        // alert(data.retmsg);
        return Promise.reject(response);
      }
      serializableSessionStorage.setItem(RELOGIN_KEY, reLoginCount + 1);
      // 尝试登录并重试请求
      auth.logout();
      // auth.login();
      const loginPromise = auth.login();
      if (IS_ZXG) {
        return loginPromise.then(() => httpClient.request(config));
      }
      return new Promise(noop);
    } else {
      return Promise.reject(response);
    }
  },
);
